import useMediaQuery from '@material-ui/core/useMediaQuery';
import React, { useMemo, useState } from 'react';
import { Atividade, Dependencia } from '../../models/atividade';
import { useMaestroContext } from '../../context/MaestroContext';
import { formatarData } from '../../utils/formatters';
import { LABEL_AREA, LABEL_TIPO_RECEITA } from '../../utils/labels';
import RiscoBadge from '../RiscoBadge';
import StatusBadge from '../StatusBadge';
import DependenciaBadge from '../DependenciaBadge';
import EstadoVazio from '../EstadoVazio';

export type AgrupamentoAtividade = 'hierarquia' | 'area' | 'responsavel' | 'empresa';

interface AtividadeTreeTableProps {
  atividades: Atividade[];
  dependencias: Dependencia[];
  agrupamento: AgrupamentoAtividade;
  onSelecionar: (id: number) => void;
}

const origemDaDependencia = (atividade: Atividade, dependencias: Dependencia[], atividades: Atividade[]): string | null => {
  if (atividade.status !== 'AGUARDANDO_DEPENDENCIA' || atividade.dependenciaIds.length === 0) return null;
  const dep = dependencias.find(d => atividade.dependenciaIds.includes(d.id));
  if (!dep) return null;
  const origem = atividades.find(a => a.id === dep.atividadeOrigemId);
  return origem?.nome ?? null;
};

const TipoReceitaTag: React.FC<{ atividade: Atividade }> = ({ atividade }) =>
  atividade.tipoReceita
    ? <span className={`mc-badge mc-badge--neutro mc-tag-receita mc-tag-receita--${atividade.tipoReceita.toLowerCase()}`}>{LABEL_TIPO_RECEITA[atividade.tipoReceita]}</span>
    : <span className='mc-texto-neutro'>—</span>;

const Linha: React.FC<{
  atividade: Atividade;
  dependencias: Dependencia[];
  atividades: Atividade[];
  nivelIndentacao?: number;
  onSelecionar: (id: number) => void;
  mostrarEmpresa?: boolean;
}> = ({ atividade, dependencias, atividades, nivelIndentacao = 0, onSelecionar, mostrarEmpresa }) => {
  const { getEmpresa, getUsuario } = useMaestroContext();
  const responsavel = getUsuario(atividade.responsavelId);
  const empresa = getEmpresa(atividade.empresaId);
  const origem = origemDaDependencia(atividade, dependencias, atividades);

  return (
    <tr className='mc-tree-row' onClick={() => onSelecionar(atividade.id)}>
      <td>
        <span className='mc-tree-row__nome' style={{ paddingLeft: nivelIndentacao * 18 }}>
          {atividade.caminhoCritico && <i className='bi bi-lightning-charge-fill mc-icone-critico' title='Caminho crítico' />}
          {atividade.nome}
        </span>
        {mostrarEmpresa && empresa && <small className='mc-tree-row__empresa'>{empresa.nome}</small>}
      </td>
      <td><TipoReceitaTag atividade={atividade} /></td>
      <td>{responsavel && <span className='mc-avatar' title={responsavel.nome}>{responsavel.iniciais}</span>}</td>
      <td>{origem ? <DependenciaBadge origemNome={origem} onClick={() => onSelecionar(atividade.id)} /> : <StatusBadge status={atividade.status} />}</td>
      <td><RiscoBadge criticidade={atividade.criticidade} /></td>
      <td>
        <div className='mc-progress'><div className='mc-progress__track'><div style={{ width: `${atividade.progresso}%` }} /></div><span>{atividade.progresso}%</span></div>
      </td>
      <td>{formatarData(atividade.dataPrevista)}</td>
    </tr>
  );
};

const CabecalhoTabela: React.FC = () => (
  <thead>
    <tr>
      <th>Atividade</th>
      <th>Receita</th>
      <th>Responsável</th>
      <th>Status</th>
      <th>Criticidade</th>
      <th>Progresso</th>
      <th>Prazo</th>
    </tr>
  </thead>
);

const CardMobile: React.FC<{
  atividade: Atividade;
  dependencias: Dependencia[];
  atividades: Atividade[];
  onSelecionar: (id: number) => void;
}> = ({ atividade, dependencias, atividades, onSelecionar }) => {
  const { getEmpresa, getUsuario } = useMaestroContext();
  const responsavel = getUsuario(atividade.responsavelId);
  const empresa = getEmpresa(atividade.empresaId);
  const origem = origemDaDependencia(atividade, dependencias, atividades);
  return (
    <div className='mc-tree-card' onClick={() => onSelecionar(atividade.id)}>
      <div className='mc-tree-card__topo'>
        <strong>{atividade.caminhoCritico && <i className='bi bi-lightning-charge-fill mc-icone-critico' />} {atividade.nome}</strong>
        <RiscoBadge criticidade={atividade.criticidade} />
      </div>
      <small>{empresa?.nome}</small>
      {atividade.tipoReceita && <TipoReceitaTag atividade={atividade} />}
      {origem ? <DependenciaBadge origemNome={origem} /> : <StatusBadge status={atividade.status} />}
      <div className='mc-progress'><div className='mc-progress__track'><div style={{ width: `${atividade.progresso}%` }} /></div><span>{atividade.progresso}%</span></div>
      <div className='mc-tree-card__rodape'>
        {responsavel && <span className='mc-avatar' title={responsavel.nome}>{responsavel.iniciais}</span>}
        <span>Prazo: {formatarData(atividade.dataPrevista)}</span>
      </div>
    </div>
  );
};

interface NoHierarquia {
  atividade: Atividade;
  filhos: NoHierarquia[];
}

const construirArvore = (atividades: Atividade[]): NoHierarquia[] => {
  const porId = new Map(atividades.map(a => [a.id, a]));
  const filhosPorPai = new Map<number | null, Atividade[]>();
  atividades.forEach(a => {
    const chave = a.parentId && porId.has(a.parentId) ? a.parentId : null;
    filhosPorPai.set(chave, [...(filhosPorPai.get(chave) ?? []), a]);
  });
  const montar = (pai: number | null): NoHierarquia[] =>
    (filhosPorPai.get(pai) ?? []).map(atividade => ({ atividade, filhos: montar(atividade.id) }));
  return montar(null);
};

const NosHierarquia: React.FC<{
  nos: NoHierarquia[];
  nivel: number;
  dependencias: Dependencia[];
  atividades: Atividade[];
  onSelecionar: (id: number) => void;
}> = ({ nos, nivel, dependencias, atividades, onSelecionar }) => (
  <>
    {nos.map(no => (
      <React.Fragment key={no.atividade.id}>
        <Linha atividade={no.atividade} dependencias={dependencias} atividades={atividades} nivelIndentacao={nivel} onSelecionar={onSelecionar} />
        {no.filhos.length > 0 && <NosHierarquia nos={no.filhos} nivel={nivel + 1} dependencias={dependencias} atividades={atividades} onSelecionar={onSelecionar} />}
      </React.Fragment>
    ))}
  </>
);

const AtividadeTreeTable: React.FC<AtividadeTreeTableProps> = ({ atividades, dependencias, agrupamento, onSelecionar }) => {
  const { getArea, getEmpresa, getUsuario } = useMaestroContext();
  const mobile = useMediaQuery('(max-width: 960px)');
  const [gruposFechados, setGruposFechados] = useState<Set<string>>(new Set());

  const toggleGrupo = (chave: string) =>
    setGruposFechados(prev => {
      const novo = new Set(prev);
      if (novo.has(chave)) novo.delete(chave); else novo.add(chave);
      return novo;
    });

  const arvore = useMemo(() => (agrupamento === 'hierarquia' ? construirArvore(atividades) : []), [agrupamento, atividades]);

  const grupos = useMemo(() => {
    if (agrupamento === 'hierarquia') return [];
    const folhas = atividades.filter(a => a.nivelHierarquico === 'TAREFA' || a.nivelHierarquico === 'SUBTAREFA');
    const chaveDoGrupo = (a: Atividade): { chave: string; titulo: string } => {
      if (agrupamento === 'area') { const area = getArea(a.areaId); return { chave: `area-${a.areaId}`, titulo: area ? LABEL_AREA[area.tipo] : 'Outras' }; }
      if (agrupamento === 'responsavel') { const u = getUsuario(a.responsavelId); return { chave: `resp-${a.responsavelId}`, titulo: u?.nome ?? 'Sem responsável' }; }
      const empresa = getEmpresa(a.empresaId);
      return { chave: `emp-${a.empresaId}`, titulo: empresa?.nome ?? 'Empresa' };
    };
    const mapa = new Map<string, { titulo: string; itens: Atividade[] }>();
    folhas.forEach(a => {
      const { chave, titulo } = chaveDoGrupo(a);
      const atual = mapa.get(chave) ?? { titulo, itens: [] };
      atual.itens.push(a);
      mapa.set(chave, atual);
    });
    return Array.from(mapa.entries()).sort((x, y) => y[1].itens.length - x[1].itens.length);
  }, [agrupamento, atividades]);

  if (atividades.length === 0) {
    return <EstadoVazio titulo='Nenhuma atividade neste escopo' descricao='Ajuste o escopo selecionado ou os filtros aplicados.' />;
  }

  if (agrupamento === 'hierarquia') {
    return mobile
      ? <div className='mc-tree-cards'>{atividades.filter(a => a.nivelHierarquico === 'TAREFA' || a.nivelHierarquico === 'SUBTAREFA').map(a => (
          <CardMobile key={a.id} atividade={a} dependencias={dependencias} atividades={atividades} onSelecionar={onSelecionar} />
        ))}</div>
      : <table className='mc-tree-table'><CabecalhoTabela /><tbody><NosHierarquia nos={arvore} nivel={0} dependencias={dependencias} atividades={atividades} onSelecionar={onSelecionar} /></tbody></table>;
  }

  return (
    <div className='mc-tree-grupos'>
      {grupos.map(([chave, grupo]) => {
        const progressoMedio = Math.round(grupo.itens.reduce((soma, a) => soma + a.progresso, 0) / grupo.itens.length);
        const fechado = gruposFechados.has(chave);
        return (
          <div className='mc-tree-grupo' key={chave}>
            <button type='button' className='mc-tree-grupo__header' onClick={() => toggleGrupo(chave)}>
              <i className={`bi bi-chevron-${fechado ? 'right' : 'down'}`} />
              <strong>{grupo.titulo}</strong>
              <span>{grupo.itens.length} atividade{grupo.itens.length > 1 ? 's' : ''}</span>
              <div className='mc-progress mc-progress--sm'><div className='mc-progress__track'><div style={{ width: `${progressoMedio}%` }} /></div><span>{progressoMedio}%</span></div>
            </button>
            {!fechado && (
              mobile
                ? <div className='mc-tree-cards'>{grupo.itens.map(a => (
                    <CardMobile key={a.id} atividade={a} dependencias={dependencias} atividades={atividades} onSelecionar={onSelecionar} />
                  ))}</div>
                : <table className='mc-tree-table'><CabecalhoTabela /><tbody>
                    {grupo.itens.map(a => <Linha key={a.id} atividade={a} dependencias={dependencias} atividades={atividades} onSelecionar={onSelecionar} mostrarEmpresa={agrupamento !== 'empresa'} />)}
                  </tbody></table>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default AtividadeTreeTable;
